import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography
} from '@mui/material'
import {TFunction} from 'i18next'
import {memo, useCallback, useMemo, useState} from 'react'
import {useFieldArray, useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {createEmptyGroup, createEmptyKpiDefinition} from '../../mappers'
import {
  KpiEditFormValues,
  KpiEditGroupFormValues,
  KpiEditTabFormValues,
  TabTypeDto
} from '../../types'

import {EditKpiGroupAccordion} from './EditKpiGroupAccordion'
import {EditKpiRow} from './EditKpiRow'
import {EditKpisSearch} from './EditKpisSearch'
import {EditKpisTabFilter} from './EditKpisTabFilter'

type TabFilterValue = TabTypeDto | 'all'

// Memoized tab section to prevent re-renders
type TabSectionProps = {
  tab: KpiEditTabFormValues
  tabIndex: number
  searchTerm: string
  t: TFunction
  plantId: string
}

const TabSectionComponent = ({tab, tabIndex, searchTerm, t, plantId}: TabSectionProps) => {
  const {control} = useFormContext<KpiEditFormValues>()

  const {
    fields: groupFields,
    remove: removeGroup,
    append: appendGroup
  } = useFieldArray({
    control,
    name: `tabGroups.${tabIndex}.groups`
  })

  const {fields: ungroupedFields, append: appendUngroupedDefinition} = useFieldArray({
    control,
    name: `tabGroups.${tabIndex}.ungroupedDefinitions`
  })

  const handleAddGroup = useCallback(() => {
    appendGroup(createEmptyGroup())
  }, [appendGroup])

  const handleAddUngroupedDefinition = useCallback(() => {
    appendUngroupedDefinition(createEmptyKpiDefinition(null))
  }, [appendUngroupedDefinition])

  const hasUngrouped = ungroupedFields.length > 0

  return (
    <Box sx={{mb: 3}}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 1}}>
        <Typography variant="h6" color="primary">
          {t(`tabs.${tab.tabType}`)}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            variant="text"
            onClick={handleAddGroup}
            data-cy="add-group-button"
          >
            {t('editKpisModal.action.addGroup')}
          </Button>
          <Button
            size="small"
            startIcon={<AddIcon />}
            variant="text"
            onClick={handleAddUngroupedDefinition}
            data-cy="add-definition-ungrouped-button"
          >
            {t('editKpisModal.action.addDefinitionUngrouped')}
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{mb: 2}} />

      {/* Groups - collapsed by default for lazy rendering */}
      {groupFields.map((field, groupIndex) => {
        // Cast field to get the actual group data (useFieldArray fields contain the current form values)
        const group = field as unknown as KpiEditGroupFormValues
        return (
          <EditKpiGroupAccordion
            key={field.id}
            tabIndex={tabIndex}
            groupIndex={groupIndex}
            definitionCount={group.definitions?.length ?? 0}
            groupName={group.name ?? ''}
            definitionNames={group.definitions?.map((d) => d.name) ?? []}
            defaultExpanded={false}
            searchTerm={searchTerm}
            onDeleteGroup={() => removeGroup(groupIndex)}
            plantId={plantId}
          />
        )
      })}

      {/* Ungrouped Definitions */}
      {hasUngrouped && (
        <UngroupedSection tabIndex={tabIndex} searchTerm={searchTerm} t={t} plantId={plantId} />
      )}
    </Box>
  )
}

const TabSection = memo(TabSectionComponent)

// Memoized ungrouped section - wrapped in accordion for lazy loading
type UngroupedSectionProps = {
  tabIndex: number
  searchTerm: string
  t: TFunction
  plantId: string
}

// Content component that only renders when accordion is expanded
const UngroupedContentComponent = ({
  tabIndex,
  searchTerm,
  plantId
}: {
  tabIndex: number
  searchTerm: string
  plantId: string
}) => {
  const {control} = useFormContext<KpiEditFormValues>()

  const {fields, remove} = useFieldArray({
    control,
    name: `tabGroups.${tabIndex}.ungroupedDefinitions`
  })

  const normalizedSearch = searchTerm.toLowerCase().trim()

  const visibleIndices = fields
    .map((field, idx) => ({field, idx}))
    .filter(({field}) => !normalizedSearch || field.name.toLowerCase().includes(normalizedSearch))
    .map(({idx}) => idx)

  return (
    <Box>
      {visibleIndices.map((defIndex) => (
        <EditKpiRow
          key={fields[defIndex]?.id ?? defIndex}
          tabIndex={tabIndex}
          groupIndex={null}
          definitionIndex={defIndex}
          onDelete={() => remove(defIndex)}
          plantId={plantId}
        />
      ))}
    </Box>
  )
}

const UngroupedContent = memo(UngroupedContentComponent)

const UngroupedSectionComponent = ({tabIndex, searchTerm, t, plantId}: UngroupedSectionProps) => {
  const {control} = useFormContext<KpiEditFormValues>()
  const [isExpanded, setIsExpanded] = useState(false)

  const {fields} = useFieldArray({
    control,
    name: `tabGroups.${tabIndex}.ungroupedDefinitions`
  })

  const normalizedSearch = searchTerm.toLowerCase().trim()

  // Check if any ungrouped definition matches the search
  const hasMatchingDefinitions =
    !normalizedSearch || fields.some((field) => field.name.toLowerCase().includes(normalizedSearch))

  if (fields.length === 0 || !hasMatchingDefinitions) return null

  return (
    <Accordion
      expanded={isExpanded}
      onChange={(_, expanded) => setIsExpanded(expanded)}
      TransitionProps={{unmountOnExit: true}}
      sx={{mt: 2}}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            justifyContent: 'space-between'
          }
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          {t('editKpisModal.ungrouped')} ({fields.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <UngroupedContent tabIndex={tabIndex} searchTerm={searchTerm} plantId={plantId} />
      </AccordionDetails>
    </Accordion>
  )
}

const UngroupedSection = memo(UngroupedSectionComponent)

type EditKpisModalViewProps = {
  open: boolean
  onClose: () => void
  onSave: () => void
  isSubmitting: boolean
  isSaveDisabled: boolean
  isDirty: boolean
  tabFilter: TabFilterValue
  onTabFilterChange: (value: TabFilterValue) => void
  searchTerm: string
  onSearchChange: (value: string) => void
  initialTabGroups: KpiEditTabFormValues[]
  plantId: string
}

export const EditKpisModalView = ({
  open,
  onClose,
  onSave,
  isSubmitting,
  isSaveDisabled,
  isDirty,
  tabFilter,
  onTabFilterChange,
  searchTerm,
  onSearchChange,
  initialTabGroups,
  plantId
}: EditKpisModalViewProps) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false)

  // Handle close with unsaved changes check
  const handleClose = useCallback(() => {
    if (isDirty) {
      setShowUnsavedChangesDialog(true)
    } else {
      onClose()
    }
  }, [isDirty, onClose])

  const handleConfirmClose = useCallback(() => {
    setShowUnsavedChangesDialog(false)
    onClose()
  }, [onClose])

  const handleCancelClose = useCallback(() => {
    setShowUnsavedChangesDialog(false)
  }, [])

  // Use static structure from initial data - no form watching needed
  const visibleTabs = useMemo(
    () =>
      initialTabGroups.filter((tab) => {
        if (tabFilter === 'all') return true
        return tab.tabType === tabFilter
      }),
    [initialTabGroups, tabFilter]
  )

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{sx: {height: '80vh'}}}
      >
        <DialogTitle sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <Typography variant="h4">{t('editKpisModal.title')}</Typography>
          <IconButton onClick={handleClose} aria-label="close-edit-modal">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{display: 'flex', flexDirection: 'column', p: 0}}>
          {/* Filters Section */}
          <Box sx={{p: 2, borderBottom: '1px solid', borderColor: 'divider'}}>
            <Stack spacing={2}>
              <EditKpisSearch value={searchTerm} onChange={onSearchChange} />
              <EditKpisTabFilter value={tabFilter} onChange={onTabFilterChange} />
            </Stack>
          </Box>

          {/* Table Header */}
          <Box
            sx={(theme) => ({
              px: 2,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'grey.800'
            })}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{flex: 2, minWidth: 200}}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('editKpisModal.columns.kpi')}
                </Typography>
              </Box>
              <Box sx={{flex: 2, minWidth: 200}}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('editKpisModal.columns.sourceKpi')}
                </Typography>
              </Box>
              <Box sx={{flex: 1, minWidth: 100}}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('editKpisModal.columns.target')}
                </Typography>
              </Box>
              <Box sx={{flex: 1, minWidth: 100}}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('editKpisModal.columns.unit')}
                </Typography>
              </Box>
              <Box sx={{flex: 1, minWidth: 120}}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('editKpisModal.columns.range')}
                </Typography>
              </Box>
              <Box sx={{width: 40}} /> {/* Space for delete button */}
            </Stack>
          </Box>

          {/* Scrollable Content */}
          <Box sx={{flex: 1, overflow: 'auto', p: 2}}>
            {visibleTabs.map((tab) => {
              const actualTabIndex = initialTabGroups.findIndex((t) => t.tabType === tab.tabType)

              return (
                <TabSection
                  key={tab.tabType}
                  tab={tab}
                  tabIndex={actualTabIndex}
                  searchTerm={searchTerm}
                  t={t}
                  plantId={plantId}
                />
              )
            })}

            {/* Empty State */}
            {visibleTabs.every(
              (tab) => tab.groups.length === 0 && tab.ungroupedDefinitions.length === 0
            ) && (
              <Box sx={{py: 8, textAlign: 'center'}}>
                <Typography color="text.secondary">{t('editKpisModal.noKpis')}</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{px: 3, py: 2}}>
          <Button onClick={handleClose} variant="text">
            {t('editKpisModal.action.cancel')}
          </Button>
          <Button onClick={onSave} variant="contained" disabled={isSaveDisabled || isSubmitting}>
            {t('editKpisModal.action.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog open={showUnsavedChangesDialog} onClose={handleCancelClose} maxWidth="xs">
        <DialogTitle>{t('editKpisModal.unsavedChanges.title')}</DialogTitle>
        <DialogContent>
          <Typography>{t('editKpisModal.unsavedChanges.message')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose} variant="text">
            {t('editKpisModal.unsavedChanges.cancel')}
          </Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            {t('editKpisModal.unsavedChanges.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
